import { action } from '@ember/object';
import { doesNotExist } from '../../../utils/spds/component-validations/helpers';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { reads } from 'macro-decorators';
import { stripBolding } from '../../../modifiers/bold-matching-text';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from '../../../utils/waiters';
import Super from './keyboard-navigable.gjs';

export default class SpdsInputMenuParent extends Super {
  classesToMap = ['disabled', 'error', 'open', 'required'];

  @tracked menuComponent;

  @reads('menuComponent.checkboxGroup') checkboxGroup;
  @reads('checkboxGroup.baseElement') checkboxList;
  @reads('menuComponent.navigableOptions', []) menuNavigableOptions;
  @reads('checkboxGroup.selected', []) selectedCheckboxes;
  @reads('selectAllComponent') selectAllOption;
  @reads('triggerComponent.baseElement') triggerContainer;

  get helperTextId() {
    return this.composeSubcomponentId('helper-text');
  }

  get labelId() {
    return this.composeSubcomponentId('label');
  }

  get menuCanOpen() {
    return this.value.length >= this.menuOpenThreshold || isEmpty(this.menuOpenThreshold);
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get menuType() {
    return this.isMultiselect ? this.multiselectMenuComponent : this.singleSelectMenuComponent;
  }

  get retainSelection() {
    return this.args.retainSelection ?? true;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  manageSingleSelectItem(target) {
    if (!target) {
      this.menuNavigableOptions.forEach(option => (option.ariaSelected = 'false'));
      this.selected = null;
      return;
    }

    this.updateSelectedItemState('false');
    this.selected = this.retainSelection || target?.id !== this.selected?.id ? target : null;
    this.updateSelectedItemState('true');
  }

  updateSelectedItemState(state) {
    this.selected?.setAttribute('aria-selected', state);
  }

  syncExternalSelection = modifier(async () => {
    if (!this.isMultiselect) return;

    let { selectedValues } = this.args;
    if (doesNotExist(selectedValues)) return;

    await waitForNext();

    this.menuNavigableOptions.forEach(option => {
      if (option.id === this.selectAllOption?.id) return;
      option.checked = selectedValues.includes(option.value);
    });

    this.checkboxList?.dispatchEvent(new CustomEvent('sync'));

    this.selected = [...selectedValues];
  });

  clearSelection = () => {
    this.checkboxList.dispatchEvent(new CustomEvent('clear'));

    this.onChange({ values: [] });

    this.trigger.focus();
  };

  handleClose = async () => {
    let shouldClose = (await Promise.resolve(this.args.onClose?.())) ?? true;

    this.open = !shouldClose;

    if (shouldClose) {
      this.menuNavigableOptions.forEach(option => option.classList.remove('active'));
    }
  };

  handleOpen = () => {
    this.open = true;
    this.args.onOpen?.();
  };

  toggle = () => {
    this.open ? this.handleClose() : this.handleOpen();
  };

  @action
  onChange({ values }) {
    if (this.isMultiselect) {
      this.selected = [...values];
    }

    this.args.onChange?.(...arguments);
  }

  @action
  onSelect({ target }) {
    let selected = target.tagName === 'STRONG' ? target.parentElement : target;

    this.manageSingleSelectItem(selected);

    this.value = this.selected ? stripBolding(this.selected) : '';

    this.handleClose();
    this.trigger.focus();

    this.args.onChange?.(...arguments);
  }
}
